import React, { useEffect, useRef } from "react";
import { defaultUserImg } from "../assets/defaultUser";

export default function UserDetailsOverlay({ 
  isVisible, 
  onClose, 
  userDetails, 
  userRole,
  fileInputRef,
  onProfilePictureChange,
  isUploadingPic,
  onMobileNumberUpdate,
  isUpdatingMobile,
  canEditMobile,
  onPasswordChange,
  isUpdatingPassword,
  canChangePassword
}) {
  const overlayRef = useRef(null);
  const [editingMobile, setEditingMobile] = React.useState(false);
  const [mobileInput, setMobileInput] = React.useState(userDetails?.mobileNo || userDetails?.MobileNo || "");
  
  // Password change state
  const [editingPassword, setEditingPassword] = React.useState(false);
  const [passwordInputs, setPasswordInputs] = React.useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [passwordError, setPasswordError] = React.useState("");

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isVisible) {
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isVisible, onClose]);

  // Sync mobile input when details change
  React.useEffect(() => {
    setMobileInput(userDetails?.mobileNo || userDetails?.MobileNo || "");
    setEditingMobile(false);
    setEditingPassword(false);
    setPasswordInputs({ currentPassword: "", newPassword: "", confirmPassword: "" });
    setPasswordError("");
  }, [userDetails?.mobileNo, userDetails?.MobileNo, isVisible]);

  // Close on background click
  const handleBackgroundClick = (e) => {
    if (overlayRef.current && e.target === overlayRef.current) {
      onClose();
    }
  };

  const handleMobileUpdate = async () => {
    if (!mobileInput.trim()) {
      alert("Mobile number cannot be empty");
      return;
    }

    if (onMobileNumberUpdate) {
      try {
        await onMobileNumberUpdate(mobileInput);
        setEditingMobile(false);
      } catch (err) {
        console.error("Failed to update mobile:", err);
      }
    }
  };

  const handlePasswordChange = async () => {
    setPasswordError("");

    // Validation
    if (!passwordInputs.currentPassword.trim()) {
      setPasswordError("Current password is required");
      return;
    }
    if (!passwordInputs.newPassword.trim()) {
      setPasswordError("New password is required");
      return;
    }
    if (!passwordInputs.confirmPassword.trim()) {
      setPasswordError("Please confirm your new password");
      return;
    }

    // Check current password matches database password
    if (passwordInputs.currentPassword !== userDetails.password) {
      setPasswordError("Current password is incorrect");
      return;
    }

    // Check new password length (8-20 characters)
    if (passwordInputs.newPassword.length < 8) {
      setPasswordError("New password must be at least 8 characters");
      return;
    }
    if (passwordInputs.newPassword.length > 20) {
      setPasswordError("New password must not exceed 20 characters");
      return;
    }

    // Check new passwords match
    if (passwordInputs.newPassword !== passwordInputs.confirmPassword) {
      setPasswordError("New passwords do not match");
      return;
    }

    // Check new password differs from current
    if (passwordInputs.newPassword === passwordInputs.currentPassword) {
      setPasswordError("New password must be different from current password");
      return;
    }

    if (onPasswordChange) {
      try {
        await onPasswordChange(
          passwordInputs.currentPassword,
          passwordInputs.newPassword
        );
        setEditingPassword(false);
        setPasswordInputs({ currentPassword: "", newPassword: "", confirmPassword: "" });
        setPasswordError("");
      } catch (err) {
        console.error("Failed to change password:", err);
        setPasswordError(err.message || "Failed to change password");
      }
    }
  };

  const handlePasswordCancel = () => {
    setEditingPassword(false);
    setPasswordInputs({ currentPassword: "", newPassword: "", confirmPassword: "" });
    setPasswordError("");
  };

  if (!isVisible || !userDetails) return null;

  const roleColors = {
    driver: "from-blue-600 to-blue-700",
    employee: "from-purple-600 to-purple-700",
    admin: "from-red-600 to-red-700",
  };

  const roleColorClass = roleColors[userRole?.toLowerCase()] || roleColors.employee;

  return (
    <div
      ref={overlayRef}
      onClick={handleBackgroundClick}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9998] p-4 animate-fade-in"
    >
      {/* Modal Card - Mobile Responsive */}
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto transform transition-all animate-slide-up">
        
        {/* Header with gradient background */}
        <div className={`bg-gradient-to-r ${roleColorClass} text-white p-6 relative overflow-hidden`}>
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl -mr-16 -mt-16"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full blur-xl -ml-12 -mb-12"></div>

          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 bg-white/20 hover:bg-white/30 rounded-full p-2 transition-colors z-10"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Profile Picture */}
          <div className="flex justify-center mb-6">
            <div className="relative group">
              <img
                src={userDetails.profilePic || defaultUserImg}
                alt={userDetails.name || "User"}
                className="w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 rounded-full border-4 border-white shadow-lg object-cover"
                onError={(e) => { e.currentTarget.src = defaultUserImg; }}
              />
              {/* Status indicator */}
              <div className="absolute bottom-3 right-3 w-5 h-5 sm:w-6 sm:h-6 bg-green-400 rounded-full border-2 border-white shadow-md animate-pulse"></div>
              
              {/* Upload button */}
              {fileInputRef && onProfilePictureChange && (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploadingPic}
                  className="absolute bottom-0 right-0 bg-blue-600 hover:bg-blue-700 text-white rounded-full p-2.5 shadow-lg transition disabled:bg-gray-400 opacity-0 group-hover:opacity-100 transform group-hover:scale-110 transition-all"
                  title="Change profile picture"
                >
                  {isUploadingPic ? (
                    <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Name and Role */}
          <div className="text-center">
            <h2 className="text-2xl sm:text-3xl font-bold mb-1">{userDetails.name || "Unknown"}</h2>
            <p className="text-white/80 font-medium text-sm sm:text-base capitalize">{userRole}</p>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          
          {/* Primary Info Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-bold text-gray-800 border-b-2 border-blue-200 pb-2">
              Personal Information
            </h3>

            {/* Service/Employee ID */}
            {(userDetails.ServiceNo || userDetails.employeeId) && (
              <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
                <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v10a2 2 0 002 2h5m4-4v2m0-6v2m0-6v2m4-2a2 2 0 012 2v6a2 2 0 01-2 2h-2.5" />
                </svg>
                <div className="flex-1">
                  <p className="text-gray-600 text-sm font-medium">ID Number</p>
                  <p className="text-gray-900 font-bold text-base">{userDetails.ServiceNo || userDetails.employeeId || "N/A"}</p>
                </div>
              </div>
            )}

            {/* Mobile Number */}
            {(userDetails.mobileNo || userDetails.MobileNo) && (
              <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg hover:bg-green-100 transition-colors">
                <svg className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 00.948.684l1.498 7.985a1 1 0 00.502.756l4.414 2.668a1 1 0 00.996 0l4.414-2.668a1 1 0 00.502-.756l1.498-7.985a1 1 0 00-.948-.684H19a2 2 0 01-2-2V5a2 2 0 01-2-2H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-2.5" />
                </svg>
                <div className="flex-1">
                  <p className="text-gray-600 text-sm font-medium">Mobile</p>
                  {editingMobile && canEditMobile ? (
                    <div className="flex gap-2 mt-2">
                      <input
                        type="tel"
                        value={mobileInput}
                        onChange={(e) => setMobileInput(e.target.value)}
                        className="flex-1 px-2 py-1 border border-green-400 rounded text-base focus:outline-none focus:ring-2 focus:ring-green-600"
                        placeholder="Enter mobile number"
                      />
                      <button
                        onClick={handleMobileUpdate}
                        disabled={isUpdatingMobile}
                        className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:bg-gray-400 transition"
                      >
                        {isUpdatingMobile ? "..." : "Save"}
                      </button>
                      <button
                        onClick={() => {
                          setEditingMobile(false);
                          setMobileInput(userDetails?.mobileNo || userDetails?.MobileNo || "");
                        }}
                        className="px-3 py-1 bg-gray-400 text-white text-sm rounded hover:bg-gray-500 transition"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <a 
                        href={`tel:${userDetails.mobileNo || userDetails.MobileNo}`}
                        className="text-green-600 font-bold hover:underline text-base"
                      >
                        {userDetails.mobileNo || userDetails.MobileNo || "N/A"}
                      </a>
                      {canEditMobile && (
                        <button
                          onClick={() => setEditingMobile(true)}
                          className="ml-2 px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 transition"
                          title="Edit mobile number"
                        >
                          Edit
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Driver-specific info */}
          {userRole?.toLowerCase() === "driver" && (
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-gray-800 border-b-2 border-blue-200 pb-2">
                Vehicle Details
              </h3>

              {userDetails.vehicleId && (
                <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg hover:bg-amber-100 transition-colors">
                  <svg className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-gray-600 text-sm font-medium">Vehicle ID</p>
                    <p className="text-gray-900 font-bold text-base">{userDetails.vehicleId}</p>
                  </div>
                </div>
              )}

              {userDetails.vehicleType && (
                <div className="flex items-start gap-3 p-3 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-colors">
                  <svg className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-gray-600 text-sm font-medium">Vehicle Type</p>
                    <p className="text-gray-900 font-bold text-base">{userDetails.vehicleType}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Password Change Section */}
          {canChangePassword && (
            <div className="border-t pt-4">
              {!editingPassword ? (
                <button
                  onClick={() => setEditingPassword(true)}
                  className="w-full px-4 py-2 bg-gradient-to-r from-orange-600 to-orange-700 text-white font-semibold rounded-lg hover:from-orange-700 hover:to-orange-800 transition-all shadow-md hover:shadow-lg"
                >
                  Change Password
                </button>
              ) : (
                <div className="space-y-3">
                  <h3 className="text-lg font-bold text-gray-800 mb-3">Change Password</h3>
                  
                  {/* Error message */}
                  {passwordError && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-red-600 text-sm font-medium">{passwordError}</p>
                    </div>
                  )}

                  {/* Current Password */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Current Password
                    </label>
                    <input
                      type="password"
                      value={passwordInputs.currentPassword}
                      onChange={(e) => {
                        setPasswordInputs((prev) => ({
                          ...prev,
                          currentPassword: e.target.value
                        }));
                        setPasswordError("");
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-600 text-sm"
                      placeholder="Enter current password"
                      disabled={isUpdatingPassword}
                    />
                  </div>

                  {/* New Password */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm font-medium text-gray-700">
                        New Password
                      </label>
                      <span className={`text-xs font-medium ${
                        passwordInputs.newPassword.length < 8 ? "text-red-600" :
                        passwordInputs.newPassword.length > 20 ? "text-red-600" :
                        "text-green-600"
                      }`}>
                        {passwordInputs.newPassword.length}/20
                      </span>
                    </div>
                    <input
                      type="password"
                      value={passwordInputs.newPassword}
                      onChange={(e) => {
                        setPasswordInputs((prev) => ({
                          ...prev,
                          newPassword: e.target.value
                        }));
                        setPasswordError("");
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-600 text-sm"
                      placeholder="Enter new password"
                      disabled={isUpdatingPassword}
                    />
                    <p className="text-xs text-gray-600 mt-1">Password must be 8-20 characters</p>
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Confirm Password
                    </label>
                    <input
                      type="password"
                      value={passwordInputs.confirmPassword}
                      onChange={(e) => {
                        setPasswordInputs((prev) => ({
                          ...prev,
                          confirmPassword: e.target.value
                        }));
                        setPasswordError("");
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-600 text-sm"
                      placeholder="Confirm new password"
                      disabled={isUpdatingPassword}
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2 pt-2">
                    <button
                      onClick={handlePasswordChange}
                      disabled={isUpdatingPassword}
                      className="flex-1 px-4 py-2 bg-orange-600 text-white font-semibold rounded-lg hover:bg-orange-700 disabled:bg-gray-400 transition-all"
                    >
                      {isUpdatingPassword ? "Updating..." : "Save Password"}
                    </button>
                    <button
                      onClick={handlePasswordCancel}
                      disabled={isUpdatingPassword}
                      className="flex-1 px-4 py-2 bg-gray-400 text-white font-semibold rounded-lg hover:bg-gray-500 disabled:bg-gray-300 transition-all"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Contact Admin Section */}
          <div className="space-y-4 border-t pt-6">
            <h3 className="text-lg font-bold text-gray-800 border-b-2 border-red-200 pb-2">
              Contact Support
            </h3>

            <div className="flex items-start gap-3 p-4 bg-red-50 rounded-lg hover:bg-red-100 transition-colors border border-red-200">
              <svg className="w-6 h-6 text-red-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.172l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              <div className="flex-1">
                <p className="text-gray-700 font-semibold mb-2">Contact Admin</p>
                <p className="text-gray-600 text-sm mb-3">For assistance, reach out to your administrator</p>
                
                <div className="flex flex-col sm:flex-row gap-2">
                  {/* Call Button */}
                  <button
                    onClick={() => {
                      // Admin phone number placeholder (will be populated when admin is assigned)
                      const adminPhone = null;
                      if (adminPhone) {
                        const cleanPhone = adminPhone.replace(/\D/g, "");
                        window.location.href = `tel:${cleanPhone}`;
                      } else {
                        alert("Admin phone number not yet assigned. Please contact your system administrator.");
                      }
                    }}
                    className="flex-1 flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-4 rounded-lg shadow-md transition transform hover:scale-105 active:scale-95"
                    title="Call Admin"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
                    </svg>
                    <span className="hidden sm:inline">Call Admin</span>
                    <span className="sm:hidden">Call</span>
                  </button>

                  {/* Message Button (optional) */}
                  <button
                    onClick={() => {
                      const adminPhone = null;
                      if (adminPhone) {
                        window.location.href = `sms:${adminPhone}`;
                      } else {
                        alert("Admin phone number not yet assigned. Please contact your system administrator.");
                      }
                    }}
                    className="flex-1 flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-700 text-white font-bold py-3 px-4 rounded-lg shadow-md transition transform hover:scale-105 active:scale-95"
                    title="Send SMS to Admin"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12h-8v-2h8v2zm0-3h-8V9h8v2zm0-3H4V4h16v4z" />
                    </svg>
                    <span className="hidden sm:inline">Message</span>
                    <span className="sm:hidden">SMS</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Status Badge */}
          <div className="flex items-center justify-center gap-2 p-4 bg-gray-100 rounded-lg">
            <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-gray-700 font-medium">Active User</span>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-2xl flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all shadow-md hover:shadow-lg active:scale-95"
          >
            Close
          </button>
        </div>
      </div>

      {/* Animations */}
      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }

        .animate-slide-up {
          animation: slide-up 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
      `}</style>
    </div>
  );
}
